import React, { useEffect, useMemo, useState } from 'react';
import { fetchSitemapData } from '../services/sitemapCrawler';

const INSPECTIONS_PER_BATCH = 50;
const INSPECTIONS_PER_WAVE = 5;

interface InspectedUrl {
  url: string;
  verdict?: string;
  coverageState?: string;
  indexingState?: string;
  lastCrawlTime?: string;
  inspectionResultLink?: string;
  error?: string;
}

interface NonIndexedUrlsPanelProps {
  siteUrl: string;
}

const SEARCH_CONSOLE_INSPECTION_URL = 'https://search.google.com/search-console?action=inspect';

const getInspectionUrl = (result: InspectedUrl) => {
  if (!result.inspectionResultLink) return SEARCH_CONSOLE_INSPECTION_URL;

  try {
    const inspectionUrl = new URL(result.inspectionResultLink);
    return inspectionUrl.origin === 'https://search.google.com'
      ? inspectionUrl.toString()
      : SEARCH_CONSOLE_INSPECTION_URL;
  } catch {
    return SEARCH_CONSOLE_INSPECTION_URL;
  }
};

const NonIndexedUrlsPanel: React.FC<NonIndexedUrlsPanelProps> = ({ siteUrl }) => {
  const [savedProgress] = useState(() => {
    try {
      const saved = localStorage.getItem(`non-indexed-url-scan:${siteUrl}`);
      return saved ? JSON.parse(saved) as { results?: InspectedUrl[]; nextIndex?: number; totalUrls?: number } : {};
    } catch {
      return {};
    }
  });
  const [sitemapUrls, setSitemapUrls] = useState<string[]>([]);
  const [totalUrls, setTotalUrls] = useState(savedProgress.totalUrls || 0);
  const [results, setResults] = useState<InspectedUrl[]>(savedProgress.results || []);
  const [nextIndex, setNextIndex] = useState(savedProgress.nextIndex || 0);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState('');

  const nonIndexedResults = useMemo(
    () => results.filter(result => !result.error && result.verdict !== 'PASS'),
    [results]
  );
  const indexedCount = results.filter(result => result.verdict === 'PASS').length;
  const failedCount = results.filter(result => Boolean(result.error)).length;
  const visibleResults = nonIndexedResults.filter(result =>
    `${result.url} ${result.coverageState || ''} ${result.indexingState || ''}`
      .toLowerCase()
      .includes(filter.toLowerCase())
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        `non-indexed-url-scan:${siteUrl}`,
        JSON.stringify({ results, nextIndex, totalUrls })
      );
    } catch (storageError) {
      console.warn('Could not save URL inspection progress locally:', storageError);
    }
  }, [siteUrl, results, nextIndex, totalUrls]);

  const scanNextBatch = async () => {
    setError('');
    setNotice('');
    setIsScanning(true);

    try {
      let urls = sitemapUrls;
      let startIndex = nextIndex;
      if (urls.length === 0) {
        const propertyForSitemap = siteUrl.startsWith('sc-domain:')
          ? `https://${siteUrl.slice('sc-domain:'.length)}`
          : siteUrl;
        const sitemap = await fetchSitemapData(propertyForSitemap);
        urls = Array.from(new Set(sitemap.urls.map(item => item.loc)));
        if (urls.length === 0) {
          throw new Error('The sitemap did not contain any page URLs.');
        }
        setSitemapUrls(urls);
        setTotalUrls(urls.length);
      }

      if (startIndex >= urls.length) {
        setNotice(`Finished inspecting ${urls.length} sitemap URLs.`);
        return;
      }
      const batchEnd = Math.min(startIndex + INSPECTIONS_PER_BATCH, urls.length);
      const inspected: InspectedUrl[] = [];

      for (let waveStart = startIndex; waveStart < batchEnd; waveStart += INSPECTIONS_PER_WAVE) {
        const wave = urls.slice(waveStart, Math.min(waveStart + INSPECTIONS_PER_WAVE, batchEnd));
        const waveResults = await Promise.all(wave.map(async url => {
          try {
            const response = await fetch('/api/gsc/url-inspection', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ siteUrl, inspectionUrl: url }),
            });
            if (!response.ok) {
              const body = await response.json().catch(() => ({}));
              throw new Error(body.error || `Inspection failed (${response.status})`);
            }

            const data = await response.json();
            const status = data.inspectionResult?.indexStatusResult || {};
            return {
              url,
              verdict: status.verdict,
              coverageState: status.coverageState,
              indexingState: status.indexingState,
              lastCrawlTime: status.lastCrawlTime,
              inspectionResultLink: data.inspectionResult?.inspectionResultLink,
              error: status.verdict ? undefined : 'Google returned no index status for this URL.',
            } as InspectedUrl;
          } catch (inspectionError) {
            return {
              url,
              error: inspectionError instanceof Error ? inspectionError.message : 'Inspection failed',
            } as InspectedUrl;
          }
        }));
        inspected.push(...waveResults);
      }

      setResults(previous => {
        const resultMap = new Map(previous.map(result => [result.url, result]));
        inspected.forEach(result => resultMap.set(result.url, result));
        return Array.from(resultMap.values());
      });
      setNextIndex(batchEnd);
      if (batchEnd < urls.length) {
        setNotice(`Inspected ${batchEnd} of ${urls.length} sitemap URLs. Continue when ready.`);
      } else {
        setNotice(`Finished inspecting ${urls.length} sitemap URLs.`);
      }
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : 'Could not scan sitemap URLs.');
    } finally {
      setIsScanning(false);
    }
  };

  const toggleUrl = (url: string) => {
    setSelectedUrls(previous => {
      const next = new Set(previous);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const toggleVisible = () => {
    setSelectedUrls(previous => {
      const next = new Set(previous);
      const allSelected = visibleResults.length > 0 && visibleResults.every(result => next.has(result.url));
      visibleResults.forEach(result => {
        if (allSelected) next.delete(result.url);
        else next.add(result.url);
      });
      return next;
    });
  };

  const openSelectedInSearchConsole = async () => {
    const selectedResults = results.filter(result => selectedUrls.has(result.url));
    if (selectedResults.length === 0) return;
    const directResults = selectedResults.filter(result => Boolean(result.inspectionResultLink));
    const copiedResults = selectedResults.filter(result => !result.inspectionResultLink);
    const tabCount = directResults.length + (copiedResults.length > 0 ? 1 : 0);
    if (tabCount > 5 && !window.confirm(`Open ${tabCount} Search Console tabs? You will need to submit each indexing request manually.`)) {
      return;
    }

    let opened = 0;
    directResults.forEach(result => {
      const tab = window.open(getInspectionUrl(result), '_blank');
      if (tab) {
        tab.opener = null;
        opened += 1;
      }
    });

    let copied = false;
    if (copiedResults.length > 0) {
      const tab = window.open(SEARCH_CONSOLE_INSPECTION_URL, '_blank');
      if (tab) {
        tab.opener = null;
        opened += 1;
      }
      try {
        await navigator.clipboard.writeText(copiedResults.map(result => result.url).join('\n'));
        copied = true;
      } catch {
        copied = false;
      }
    }

    const noticeParts = [];
    if (opened > 0) noticeParts.push(`Opened ${opened} Search Console tab${opened === 1 ? '' : 's'}.`);
    if (copiedResults.length > 0) {
      noticeParts.push(copied
        ? `${copiedResults.length} URL${copiedResults.length === 1 ? '' : 's'} without a Google-provided link ${copiedResults.length === 1 ? 'was' : 'were'} copied; paste ${copiedResults.length === 1 ? 'it' : 'them'} into the inspection bar.`
        : `Open Search Console and paste these ${copiedResults.length} URL${copiedResults.length === 1 ? '' : 's'} into the inspection bar.`);
    }
    if (directResults.length > 0) {
      noticeParts.push('Submit any indexing requests manually in Search Console.');
    }
    setNotice(noticeParts.join(' '));
  };

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Non-indexed URLs</h2>
          <p className="text-sm text-gray-600 mt-1">
            Inspect sitemap pages using Google Search Console. The Google API can report indexing status,
            but indexing requests for ordinary pages must still be submitted manually in Search Console.
          </p>
        </div>
        <button
          type="button"
          onClick={scanNextBatch}
          disabled={isScanning || (sitemapUrls.length > 0 && nextIndex >= sitemapUrls.length)}
          className="shrink-0 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {isScanning
            ? 'Inspecting URLs…'
            : sitemapUrls.length === 0
              ? `Scan sitemap (up to ${INSPECTIONS_PER_BATCH})`
              : nextIndex < sitemapUrls.length
                ? `Scan next ${Math.min(INSPECTIONS_PER_BATCH, sitemapUrls.length - nextIndex)} URLs`
                : 'Sitemap scan complete'}
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Scans up to {INSPECTIONS_PER_BATCH} URLs per batch to help manage Google’s URL Inspection API quota
        (documented at 2,000 inspections per day per property). Progress is saved in this browser.
      </p>

      {error && <div role="alert" className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="mb-4 rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">{notice}</div>}

      {results.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="rounded-md bg-gray-50 p-3">
              <div className="text-xs text-gray-500">Inspected</div>
              <div className="text-lg font-semibold text-gray-900">{results.length} / {totalUrls || '—'}</div>
            </div>
            <div className="rounded-md bg-green-50 p-3">
              <div className="text-xs text-green-700">Indexed</div>
              <div className="text-lg font-semibold text-green-800">{indexedCount}</div>
            </div>
            <div className="rounded-md bg-amber-50 p-3">
              <div className="text-xs text-amber-700">Not indexed</div>
              <div className="text-lg font-semibold text-amber-800">{nonIndexedResults.length}</div>
            </div>
            <div className="rounded-md bg-red-50 p-3">
              <div className="text-xs text-red-700">Inspection errors</div>
              <div className="text-lg font-semibold text-red-800">{failedCount}</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
            <input
              type="search"
              value={filter}
              onChange={event => setFilter(event.target.value)}
              placeholder="Filter non-indexed URLs"
              className="w-full sm:max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm"
              aria-label="Filter non-indexed URLs"
            />
            <span className="text-sm text-gray-600">
              {selectedUrls.size} selected
            </span>
            <button
              type="button"
              onClick={openSelectedInSearchConsole}
              disabled={selectedUrls.size === 0}
              className="sm:ml-auto px-3 py-2 rounded-md border border-blue-300 text-blue-700 text-sm hover:bg-blue-50 disabled:text-gray-400 disabled:border-gray-200"
            >
              Open selected in Search Console
            </button>
          </div>

          {visibleResults.length > 0 ? (
            <div className="overflow-x-auto border border-gray-200 rounded-md">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">
                      <input
                        type="checkbox"
                        aria-label="Select all visible non-indexed URLs"
                        checked={visibleResults.length > 0 && visibleResults.every(result => selectedUrls.has(result.url))}
                        onChange={toggleVisible}
                      />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">URL</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Google’s status</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Last crawl</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleResults.map(result => (
                    <tr key={result.url} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedUrls.has(result.url)}
                          onChange={() => toggleUrl(result.url)}
                          aria-label={`Select ${result.url}`}
                        />
                      </td>
                      <td className="px-3 py-2 max-w-lg break-all">
                        <a href={result.url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                          {result.url}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {result.coverageState || result.indexingState || result.verdict || 'No status details'}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {result.lastCrawlTime ? new Date(result.lastCrawlTime).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <a
                          href={getInspectionUrl(result)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            if (!result.inspectionResultLink) {
                              navigator.clipboard?.writeText(result.url).then(
                                () => setNotice('URL copied. Paste it into the Search Console inspection bar.'),
                                () => setNotice('Paste this URL into the Search Console inspection bar: ' + result.url)
                              );
                            }
                          }}
                          className="text-blue-700 hover:underline"
                        >
                          Inspect in GSC
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="rounded-md bg-green-50 p-4 text-sm text-green-800">
              {nonIndexedResults.length === 0
                ? 'No non-indexed URLs found in the URLs inspected so far.'
                : 'No non-indexed URLs match your filter.'}
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default NonIndexedUrlsPanel;
